import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import ChoshiiLogo from '../../ChoshiiLogo';
import NotificationSign from '../../components/NotificationSign';
import { useTranslation } from 'react-i18next';

const GOLD = '#D4AF37';
const TEXT_DEFAULT = '#ccc';
const BG = '#000';
const BORDER = '#222';
const RED = '#D24444';

const SettingsSidebarAd: React.FC = () => {
  const navigate = useNavigate();
  const loc = useLocation();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const isActive = (match: string | string[]) => {
    const arr = Array.isArray(match) ? match : [match];
    return arr.some(p =>
      p === '/' ? loc.pathname === '/' : loc.pathname.startsWith(p)
    );
  };

  const Button: React.FC<{
    to?: string;
    onClick?: () => void;
    activeMatch?: string | string[];
    children: React.ReactNode;
  }> = ({ to, onClick, activeMatch, children }) => {
    const [hovered, setHovered] = useState(false);
    const active = activeMatch ? isActive(activeMatch) : !!(to && isActive(to));
    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: 12,
      background: BG, // ✅ always dark
      color: TEXT_DEFAULT, // ✅ always light
      border: `1px solid ${active ? GOLD : hovered ? GOLD : BORDER}`, // ✅ active = permanent gold border
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease',
      // optional: subtle “locked in” outline without fill
      // boxShadow: active ? `0 0 0 1px ${GOLD}` : 'none',
    };
    
    return (
      <button
        style={style}    
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => (to ? navigate(to) : onClick?.())}
      >
        {children}
      </button>
    );
  };

  const DangerButton: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({
    onClick,
    children,
  }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <button
        onClick={onClick}
        style={{
          display: 'block',
          width: '100%',
          padding: '8px 10px',
          marginBottom: 12,
          background: BG,
          color: TEXT_DEFAULT,
          border: `1px solid ${hovered ? RED : BORDER}`,
          borderRadius: 8,
          cursor: 'pointer',
          fontWeight: 500,
          textAlign: 'left',
          fontSize: 14,
          transition: 'border-color 120ms ease',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
      >
        {children}
      </button>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/ads-home');
  };
  return (
    <aside style={wrap}>
      {/* header: logo + bell – MATCHED to MyProfile (bell does NOT affect logo) */}
      <div
        style={{
          position: "relative",      // ✅ anchor for absolute bell
          display: "flex",
          alignItems: "center",
          padding: "12px 20px",
          marginBottom: "-34px",
          marginTop: "-2.7%",
          color: "#fff",
          minHeight: 64,             // ✅ gives room so it won’t overlap
        }}
      >
        {/* Logo (left, normal flow) */}
        <div style={{ marginLeft: "-25%", marginTop: "9%", marginBottom: "13%" }}>
          <ChoshiiLogo
            animateSheen
            fontFamily="'Playfair Display', serif"
            style={{ width: "70%", cursor: "pointer" }}
            padding={0}
            onClick={() => navigate("/advertisementstart")}
            clickTrimRatio={0.12}
          />
        </div>
  
        {/* Notification (independent, pinned right) */}
        <div
          style={{
            position: "absolute",   // ✅ does NOT affect logo
            top: 42,                // tweak if needed
            right: 67,              // ✅ move LEFT by reducing this (20, 10, 0, -5)
            display: "flex",
            alignItems: "center",
          }}
        >
          <NotificationSign to="/notifications" />
        </div>
      </div>
  
      <hr style={hr} />
  
      <div style={sectionTitle}>{t("settingsSidebar.sections.settings")}</div>
      <nav>
        <Button to="/ad/settings/overview" activeMatch="/ad/settings/overview">
          🧭 {t("settingsSidebar.buttons.overview", "Overview")}
        </Button>
        <Button to="/ad/settings/account" activeMatch="/ad/settings/account">
          👤 {t("settingsSidebar.buttons.account")}
        </Button>
        <Button to="/ad/settings/privacy" activeMatch="/ad/settings/privacy">
          🔒 {t("settingsSidebar.buttons.dataPrivacy")}
        </Button>
        <Button to="/ad/settings/security" activeMatch="/ad/settings/security">
          🛡️ {t("settingsSidebar.buttons.security")}
        </Button>
        <Button
          to="/ad/settings/profile-management"
          activeMatch="/ad/settings/profile-management"
        >
          🧩 {t("settingsSidebar.buttons.profileManagement")}
        </Button>
        <Button to="/helpsupportstart" activeMatch="/helpsupportstart">
          🛟 {t("settingsSidebar.buttons.helpSupport")}
        </Button>
      </nav>
  
      <hr
        style={{
          margin: "16px 0",
          borderColor: "#333",
          marginTop: "20px",
          marginBottom: "20px",
        }}
      />
  
      <div style={sectionTitle}>{t("mainSidebar.buttons.ads")}</div>
      <Button onClick={() => navigate("/advertisementstart")}>
        🛍️ {t("mainSidebar.buttons.ads")}
      </Button>
  
      <hr
        style={{
          margin: "16px 0",
          borderColor: "#333",
          marginTop: "20px",
          marginBottom: "20px",
        }}
      />
  
      <Button onClick={() => navigate("/managebillings")}>
        💵 {t("ad.start.billing.manageBillings")}
      </Button>
      <Button onClick={() => navigate("/changepayment")}>
        💳 {t("ad.start.billing.changePayment")}
      </Button>
      <Button onClick={() => navigate("/adregistration")}>
        📒 "Ad Registration"
      </Button>
  
      <hr
        style={{
          margin: "16px 0",
          borderColor: "#333",
          marginTop: "20px",
          marginBottom: "20px",
        }}
      />
  
      <DangerButton onClick={handleLogout}>
        📕 {t("settingsSidebar.buttons.logout")}
      </DangerButton>
    </aside>
  );
  
};

export default SettingsSidebarAd;

/* styles */
const wrap: React.CSSProperties = {
  width: '230px',
  background:
    'linear-gradient(145deg, rgba(5, 9, 20, 0.95), rgba(4, 7, 15, 0.95))',
  color: '#bbb',
  padding: '20px',
  height: 'calc(100vh)',
  boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  position: 'fixed',
  marginTop: '-2%',
  overflow: 'hidden',
};

const hr: React.CSSProperties = {
  border: 0,
  borderTop: `1px solid ${BORDER}`,
  margin: '14px 0',
};

const sectionTitle: React.CSSProperties = {
  color: '#bbb',
  fontWeight: 700,
  marginBottom: 10,
  fontSize: 13,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};
